import React, {useMemo} from "react";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {toLineChartPoints} from "../../chart/ServiceDashboardWidgets";

const CHART_BLUE = "#3b82f6";
const CHART_PURPLE = "#6B5CA5";
const AXIS_COLOR = "#666666";
const GRID_COLOR = "#e8e8e8";

const truncateLabel = (name, max = 5) => {
    if (!name) return "";
    return name.length > max ? `${name.slice(0, max)}…` : name;
};

const formatYAxis = (value) => {
    if (value >= 1000) return `${Math.round(value / 1000)}k`;
    return value;
};

function AccessLogChartTooltip({active, payload, label, valueLabel = "건수"}) {
    if (!active || !payload?.length) return null;
    const value = payload[0]?.value ?? payload[0]?.payload?.cnt ?? 0;
    return (
        <div className="rounded-[4px] border border-[rgba(0,0,0,0.08)] bg-white px-3 py-2 text-[12px] shadow-sm">
            <div className="font-medium text-[#333]">{label}</div>
            <div className="text-[#666]">
                {valueLabel} : {Number(value).toLocaleString()}
            </div>
        </div>
    );
}

function EmptyChartState() {
    return (
        <div className="flex h-full items-center justify-center bg-[#fafafa] text-[13px] text-[#999]">
            No Data.
        </div>
    );
}

/** 일자별 접속 추이 — ManagedAssetLineArea / ServiceTrendLineChart 톤 */
export function AccessLogTrendChart({lineChart}) {
    const points = useMemo(
        () => toLineChartPoints(lineChart ? [lineChart] : null),
        [lineChart]
    );

    const yMax = useMemo(() => {
        const maxVal = Math.max(...points.map((p) => p.value), 0);
        if (maxVal <= 0) return 10;
        return Math.ceil(maxVal * 1.15) || 10;
    }, [points]);

    if (!points.some((p) => p.value > 0)) {
        return <EmptyChartState />;
    }

    return (
        <div className="access-log-chart access-log-chart--trend [&_*:focus]:outline-none">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={points} margin={{top: 12, right: 16, left: 4, bottom: 0}}>
                    <defs>
                        <linearGradient id="accessLogAreaGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={CHART_PURPLE} stopOpacity={0.35}/>
                            <stop offset="100%" stopColor={CHART_PURPLE} stopOpacity={0.03}/>
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="4 4" vertical={false} stroke={GRID_COLOR}/>
                    <XAxis
                        dataKey="name"
                        tick={{fontSize: 10, fill: "#888"}}
                        tickFormatter={(value) => truncateLabel(value, 5)}
                        interval="preserveStartEnd"
                        height={28}
                        tickMargin={4}
                        axisLine={{stroke: AXIS_COLOR, strokeWidth: 1}}
                        tickLine={false}
                    />
                    <YAxis
                        width={36}
                        tick={{fontSize: 11, fill: "#888"}}
                        tickFormatter={formatYAxis}
                        domain={[0, yMax]}
                        allowDecimals={false}
                        axisLine={{stroke: AXIS_COLOR, strokeWidth: 1}}
                        tickLine={false}
                    />
                    <Tooltip content={<AccessLogChartTooltip valueLabel="접속"/>}/>
                    <Area
                        type="monotone"
                        dataKey="value"
                        stroke={CHART_PURPLE}
                        strokeWidth={2}
                        fill="url(#accessLogAreaGradient)"
                        dot={{r: 3, fill: CHART_PURPLE, stroke: "#fff", strokeWidth: 2}}
                        activeDot={{r: 5}}
                        isAnimationActive={false}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
}

/** 업체별 접속 — ServiceBarChartCard 톤 */
export function AccessLogCompanyBarChart({items}) {
    const chartData = useMemo(() => [...(items ?? [])], [items]);

    const yMax = useMemo(() => {
        const maxCnt = Math.max(...chartData.map((d) => Number(d.cnt) || 0), 0);
        if (maxCnt <= 0) return 10;
        return Math.ceil(maxCnt * 1.15) || 10;
    }, [chartData]);

    if (!chartData.some((row) => Number(row.cnt) > 0)) {
        return <EmptyChartState />;
    }

    return (
        <div className="access-log-chart access-log-chart--bar [&_*:focus]:outline-none">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{top: 12, right: 16, left: 4, bottom: 0}}>
                    <CartesianGrid strokeDasharray="4 4" vertical={false} stroke={GRID_COLOR}/>
                    <XAxis
                        dataKey="name"
                        tick={{fontSize: 10, fill: "#888"}}
                        tickFormatter={(value) => truncateLabel(value, 4)}
                        interval={0}
                        angle={-35}
                        textAnchor="end"
                        height={36}
                        tickMargin={2}
                        axisLine={{stroke: AXIS_COLOR, strokeWidth: 1}}
                        tickLine={false}
                    />
                    <YAxis
                        domain={[0, yMax]}
                        allowDecimals={false}
                        tick={{fontSize: 11, fill: "#888"}}
                        width={32}
                        tickFormatter={formatYAxis}
                        axisLine={{stroke: AXIS_COLOR, strokeWidth: 1}}
                        tickLine={false}
                    />
                    <Tooltip content={<AccessLogChartTooltip/>} cursor={{fill: "transparent"}}/>
                    <Bar dataKey="cnt" fill={CHART_BLUE} radius={[4, 4, 0, 0]} maxBarSize={48}/>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}
